import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { AudienceMember, AudienceSummary, Lead } from '@/api/types';
import { AUDIENCE_STATUS_LABELS, type SegmentDefinition } from '@marketing-hub/shared';
import { AudienceSegmentEditor } from '@/features/audiences/AudienceSegmentEditor';
import { AudienceScraperPanel } from '@/features/audiences/AudienceScraperPanel';
import { AudienceAccountsPanel } from '@/features/audiences/AudienceAccountsPanel';
import { AudienceInsightsPanel } from '@/features/audiences/AudienceInsightsPanel';
import { AudienceScoresPanel } from '@/features/audiences/AudienceScoresPanel';
import { AudienceAiPanel } from '@/features/audiences/AudienceAiPanel';
import { AudienceSubaudiencesPanel } from '@/features/audiences/AudienceSubaudiencesPanel';
import { AudienceCampaignKpiPanel } from '@/features/audiences/AudienceCampaignKpiPanel';
import { AudienceBrevoPanel } from '@/features/audiences/AudienceBrevoPanel';
import type { IntegrationPublic } from '@marketing-hub/shared';
import { formatAudienceReplyRatePct } from '@marketing-hub/shared';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { AudiencesFlagGate } from '@/features/audiences/AudiencesFlagGate';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import leadStyles from '@/features/leads/Leads.module.css';
import styles from '../ProjectsPage.module.css';

type Tab =
  | 'overzicht'
  | 'filters'
  | 'scraper'
  | 'bedrijven'
  | 'inzichten'
  | 'ai'
  | 'contacten'
  | 'campagnes';

export function DoelgroepDetailPage() {
  const { audienceId } = useParams<{ audienceId: string }>();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('overzicht');
  const [leadQ, setLeadQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const audQ = useQuery({
    queryKey: ['audience', audienceId],
    enabled: Boolean(audienceId),
    queryFn: () => apiFetch<{ audience: AudienceSummary }>(`/api/audiences/${audienceId}`),
  });

  const membersQ = useQuery({
    queryKey: ['audience-members', audienceId],
    enabled: Boolean(audienceId) && tab === 'contacten',
    queryFn: () =>
      apiFetch<{ members: AudienceMember[] }>(`/api/audiences/${audienceId}/members?limit=200`),
  });

  const projectId = audQ.data?.audience.project_id;

  const integrationsQ = useQuery({
    queryKey: ['integrations', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ integrations: IntegrationPublic[] }>(
        `/api/integrations?project_id=${projectId}`,
      ),
  });

  const brevoIntegrationId =
    integrationsQ.data?.integrations.find(
      (i) => i.connector_type === 'brevo' && i.status === 'active',
    )?.id ?? null;

  const leadsQ = useQuery({
    queryKey: ['leads-pick', projectId, leadQ],
    enabled: Boolean(projectId) && tab === 'contacten',
    queryFn: () => {
      const params = new URLSearchParams({
        project_id: projectId!,
        limit: '50',
        offset: '0',
      });
      if (leadQ.trim()) params.set('q', leadQ.trim());
      return apiFetch<{ leads: Lead[] }>(`/api/leads?${params}`);
    },
  });

  const campaignsQ = useQuery({
    queryKey: ['campaigns', projectId],
    enabled: Boolean(projectId) && tab === 'campagnes',
    queryFn: () => apiFetch<{ campaigns: { id: string; name: string; status: string; audience_id?: string | null }[] }>(
      `/api/campaigns?project_id=${projectId}`,
    ),
  });

  const linkedCampaigns = useMemo(
    () => (campaignsQ.data?.campaigns ?? []).filter((c) => c.audience_id === audienceId),
    [campaignsQ.data, audienceId],
  );

  const addMembers = useMutation({
    mutationFn: (leadIds: string[]) =>
      apiFetch(`/api/audiences/${audienceId}/members`, {
        method: 'POST',
        body: JSON.stringify({ lead_ids: leadIds, source: 'manual' }),
      }),
    onSuccess: () => {
      setPicked(new Set());
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  const removeMember = useMutation({
    mutationFn: (leadId: string) =>
      apiFetch(`/api/audiences/${audienceId}/members/${leadId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
    },
  });

  const syncList = useMutation({
    mutationFn: () =>
      apiFetch<{ list_id: string; member_count: number }>(
        `/api/audiences/${audienceId}/sync-list`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lists'] }),
  });

  if (!audienceId) return null;

  return (
    <ProjectGate requireSelection>
      <AudiencesFlagGate>
        {audQ.isLoading ? <LoadingState /> : null}
        {audQ.isError ? <ErrorState message={(audQ.error as Error).message} /> : null}
        {audQ.data ? (
          <>
            <p className={styles.mutedSmall}>
              <Link to="/app/doelgroepen">Doelgroepen</Link>
              {audQ.data.audience.parent_audience_id && audQ.data.audience.parent_name ? (
                <>
                  {' · '}
                  <Link to={`/app/doelgroepen/${audQ.data.audience.parent_audience_id}`}>
                    {audQ.data.audience.parent_name}
                  </Link>
                </>
              ) : null}
              {' · '}
              <span>{audQ.data.audience.name}</span>
            </p>
            <header style={{ marginBottom: 'var(--space-lg)' }}>
              <h1>{audQ.data.audience.name}</h1>
              <p className={styles.mutedSmall}>
                {AUDIENCE_STATUS_LABELS[
                  audQ.data.audience.status as keyof typeof AUDIENCE_STATUS_LABELS
                ] ?? audQ.data.audience.status}
                {' · '}
                {audQ.data.audience.lead_count} contacten · {audQ.data.audience.account_count}{' '}
                bedrijven
                {(audQ.data.audience.kpi_enrollments ?? 0) > 0 ? (
                  <>
                    {' · '}
                    Response {formatAudienceReplyRatePct(audQ.data.audience.kpi_reply_rate ?? undefined)}
                  </>
                ) : null}
              </p>
            </header>

            <nav className={styles.row} aria-label="Doelgroep tabs">
              {(
                [
                  'overzicht',
                  'filters',
                  'scraper',
                  'bedrijven',
                  'inzichten',
                  'ai',
                  'contacten',
                  'campagnes',
                ] as const
              ).map((t) => (
                <Button
                  key={t}
                  type="button"
                  variant={tab === t ? 'secondary' : 'ghost'}
                  onClick={() => setTab(t)}
                >
                  {t === 'overzicht'
                    ? 'Overzicht'
                    : t === 'filters'
                      ? 'Filters'
                      : t === 'scraper'
                        ? 'Scraper'
                        : t === 'bedrijven'
                          ? 'Bedrijven'
                          : t === 'inzichten'
                            ? 'Data & inzichten'
                            : t === 'ai'
                              ? 'AI-advies'
                              : t === 'contacten'
                                ? 'Contacten'
                                : 'Campagnes'}
                </Button>
              ))}
            </nav>

            {tab === 'overzicht' ? (
              <>
                <section className={styles.card}>
                  {audQ.data.audience.description ? (
                    <p>{audQ.data.audience.description}</p>
                  ) : (
                    <p className={styles.mutedSmall}>Geen omschrijving.</p>
                  )}
                  <p className={styles.mutedSmall}>
                    Type:{' '}
                    {audQ.data.audience.audience_type === 'dynamic' ? 'Dynamisch' : 'Statisch'}
                  </p>
                  <p className={styles.mutedSmall}>
                    Campagnelijst:{' '}
                    {audQ.data.audience.list_id ? (
                      <Link to="/app/campagnes/bibliotheek">Bibliotheek (gesynchroniseerd)</Link>
                    ) : (
                      '—'
                    )}
                  </p>
                  <div className={styles.row}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => syncList.mutate()}
                      disabled={syncList.isPending}
                    >
                      Lijst synchroniseren
                    </Button>
                    <Link to="/app/campagnes/nieuw">
                      <Button type="button">Nieuwe campagne</Button>
                    </Link>
                  </div>
                </section>
                {projectId ? (
                  <AudienceBrevoPanel audienceId={audienceId} integrationId={brevoIntegrationId} />
                ) : null}
                <AudienceScoresPanel audienceId={audienceId} />
                <AudienceSubaudiencesPanel
                  audienceId={audienceId}
                  isSubAudience={Boolean(audQ.data.audience.parent_audience_id)}
                  childrenCount={audQ.data.audience.children_count ?? 0}
                  rollupLeadCount={audQ.data.audience.rollup_lead_count}
                />
                <AudienceCampaignKpiPanel
                  campaignsLinked={audQ.data.audience.kpi_campaigns_linked ?? 0}
                  enrollments={audQ.data.audience.kpi_enrollments ?? 0}
                  replies={audQ.data.audience.kpi_replies ?? 0}
                  bounces={audQ.data.audience.kpi_bounces ?? 0}
                  mailsSent={audQ.data.audience.kpi_mails_sent ?? 0}
                  replyRate={audQ.data.audience.kpi_reply_rate ?? null}
                />
              </>
            ) : null}

            {tab === 'scraper' ? (
              <AudienceScraperPanel
                audienceId={audienceId}
                audienceName={audQ.data.audience.name}
              />
            ) : null}

            {tab === 'bedrijven' && projectId ? (
              <AudienceAccountsPanel audienceId={audienceId} projectId={projectId} />
            ) : null}

            {tab === 'inzichten' ? <AudienceInsightsPanel audienceId={audienceId} /> : null}

            {tab === 'ai' ? <AudienceAiPanel audienceId={audienceId} /> : null}

            {tab === 'filters' && projectId ? (
              <AudienceSegmentEditor
                audienceId={audienceId}
                projectId={projectId}
                initialDefinition={
                  (audQ.data.audience.segment_definition as SegmentDefinition | null) ?? null
                }
                audienceType={audQ.data.audience.audience_type}
                refreshEnabled={Boolean(audQ.data.audience.refresh_enabled)}
                refreshRemoveNonMatching={
                  audQ.data.audience.refresh_remove_non_matching !== false
                }
                lastRefreshedAt={audQ.data.audience.last_refreshed_at ?? null}
              />
            ) : null}

            {tab === 'contacten' ? (
              <section className={styles.card}>
                <h2>Contacten in doelgroep</h2>
                {membersQ.isLoading ? <LoadingState /> : null}
                <ul className={styles.list}>
                  {(membersQ.data?.members ?? []).map((m) => (
                    <li key={m.lead_id} className={styles.row}>
                      <span>
                        {m.contact_name ?? m.email ?? m.lead_id}{' '}
                        <span className={styles.mutedSmall}>{m.email}</span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeMember.mutate(m.lead_id)}
                        disabled={removeMember.isPending}
                      >
                        Verwijderen
                      </Button>
                    </li>
                  ))}
                </ul>

                <h3 style={{ marginTop: 'var(--space-lg)' }}>Toevoegen uit leads</h3>
                <Field label="Zoeken">
                  <TextInput value={leadQ} onChange={(e) => setLeadQ(e.target.value)} />
                </Field>
                {leadsQ.isLoading ? <LoadingState /> : null}
                <ul className={styles.list}>
                  {(leadsQ.data?.leads ?? []).map((l) => (
                    <li key={l.id}>
                      <label className={leadStyles.filterLabel}>
                        <input
                          type="checkbox"
                          checked={picked.has(l.id)}
                          onChange={(e) => {
                            setPicked((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(l.id);
                              else next.delete(l.id);
                              return next;
                            });
                          }}
                        />{' '}
                        {l.contact_name ?? l.email} · {l.company ?? '—'}
                      </label>
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  disabled={picked.size === 0 || addMembers.isPending}
                  onClick={() => addMembers.mutate([...picked])}
                >
                  {picked.size} contact(en) toevoegen
                </Button>
              </section>
            ) : null}

            {tab === 'campagnes' ? (
              <section className={styles.card}>
                <h2>Campagnes op deze doelgroep</h2>
                {campaignsQ.isLoading ? <LoadingState /> : null}
                {!linkedCampaigns.length ? (
                  <StateMessage title="Geen gekoppelde campagnes">
                    Maak een campagne en kies deze doelgroep in de wizard.
                  </StateMessage>
                ) : (
                  <ul className={styles.list}>
                    {linkedCampaigns.map((c) => (
                      <li key={c.id}>
                        <Link to={`/app/campagnes/${c.id}`}>{c.name}</Link>
                        <span className={styles.mutedSmall}> · {c.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}
          </>
        ) : null}
      </AudiencesFlagGate>
    </ProjectGate>
  );
}
